import { useEffect, useRef } from "react";

import {
	COPYWRITING_COMPANY_GENERATION_UPDATE,
	COPYWRITING_JOIN_COMPANY_GENERATIONS,
	companyGenerationUpdateSchema,
} from "@signalist/shared-types";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { api } from "@/redux/api";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { copywritingOrchestrationApi } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import {
	applyCompanyGenerationUpdate,
	copywritingOrchestrationSelector,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

/** Refetch `GET /active` — the fully-hydrated job list — via tag invalidation. */
const refetchActiveGenerations = () =>
	copywritingOrchestrationApi.util.invalidateTags(["CopywritingOrchestration"]);

/**
 * Company-room copywriting socket (PREC-3635 P6 D8): joins the company
 * generations room and keeps the slice's job map fresh with the compact
 * lifecycle updates. An update for a prospect not in the map carries no
 * display fields, so it triggers a `GET /active` refetch instead (D2) — the
 * same refetch runs on every (re)connect to cover missed events.
 *
 * Must be mounted at the Feed page root (never inside a tab-gated component)
 * so the socket survives tab switches — the `useFeedDraftGeneration` lesson.
 */
export const useCompanyGenerationsSocket = () => {
	const dispatch = useAppDispatch();
	const { token, user } = useAppSelector(authSelector);
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const companyId = user?.company?.id;

	// The known-prospect check reads through a ref so socket callbacks always see
	// the latest map without reconnecting on every job change.
	const { jobsByProspectId } = useAppSelector(copywritingOrchestrationSelector);
	const jobsRef = useRef(jobsByProspectId);

	useEffect(() => {
		jobsRef.current = jobsByProspectId;
	}, [jobsByProspectId]);

	useEffect(() => {
		if (!token || !companyId) {
			return;
		}

		const socket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		socket.on("connect", () => {
			socket.emit(COPYWRITING_JOIN_COMPANY_GENERATIONS, { companyId });
			// Reconnect gap cover (D8): events missed while disconnected never
			// replay, so re-sync the whole map from the source of truth.
			dispatch(refetchActiveGenerations());
		});

		socket.on("connect_error", (error) => {
			console.warn("[CompanyGenerationsSocket] Connection error:", error);
		});

		socket.on(COPYWRITING_COMPANY_GENERATION_UPDATE, (raw: unknown) => {
			const parsed = companyGenerationUpdateSchema.safeParse(raw);

			if (!parsed.success) {
				return;
			}

			const update = parsed.data;

			// A finished generation persisted fresh drafts to the list_person. Refresh its
			// cached record so an already-fetched (draft-less) ListPerson — e.g. one the
			// reach-out menu loaded before generation — doesn't leave the docked chat empty
			// until a manual reload (PREC-3710). The Chat tab reads getSingleListPerson.
			if (update.status === "COMPLETED") {
				dispatch(api.util.invalidateTags([{ type: "ListPerson", id: update.listPersonId }]));
			}

			// FAILED only ever removes; a removal for an unknown prospect is a no-op,
			// so it never needs the hydrating refetch below.
			if (update.status === "FAILED" || jobsRef.current[update.prospectId]) {
				dispatch(applyCompanyGenerationUpdate(update));

				return;
			}

			dispatch(refetchActiveGenerations());
		});

		return () => {
			socket.disconnect();
		};
	}, [token, companyId, dispatch]);
};
